import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import BrowserEvents from "const/BrowserEvents";
import DropDownContext from "nlib/ui/DropDown/DropDownContext";
import Platforms from "utils/Platforms";
import Portal from "nlib/ui/Portal";
import React, { Fragment, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import useWindowResize from "hooks/useWindowResize";

const CONTENT_INDENT = 16;

const DOUBLE = 2;

const INSTANCES = [];

const Content = (props) => {
  const innerRef = useRef();

  const {
    children,
    alignRight,
    modal,
    portal = modal,
    title,
    centered = true,
    className,
    topOffset = 0,
    dropdownRef = innerRef,
    ...restProps
  } = props;

  const { setOpened, containerRef, toggleRef } = useContext(DropDownContext);

  const [{
    right: overflowRight,
    top: overflowTop,
    bottom: overflowBottom
  }, setOverflow] = useState({ right: 0, top: 0, bottom: 0 });

  const style = useMemo(() => {
    const container = containerRef && containerRef.current;

    if (!container) return {};

    const { innerHeight: windowHeight, innerWidth: windowWidth } = window;

    if (modal) {
      return Platforms.checkIsTouchDevice() && centered ? {
        maxHeight: windowHeight - (CONTENT_INDENT * DOUBLE),
        maxWidth: windowWidth - (CONTENT_INDENT * DOUBLE)
      } : {};
    }

    const { top, left, right, bottom, height, width } = container.getBoundingClientRect();

    // eslint-disable-next-line no-magic-numbers
    const onTop = top > (windowHeight + height) / 2;

    const maxHeight = onTop ? top - CONTENT_INDENT - topOffset : windowHeight - bottom - CONTENT_INDENT;

    if (portal) {
      return {
        minWidth: width,
        top: onTop ? "auto" : bottom + overflowBottom,
        left: alignRight ? right - overflowRight : left - overflowRight,
        transform: alignRight ? "translateX(-100%)" : undefined,
        bottom: onTop ? windowHeight - top + overflowTop : "auto",
        maxHeight
      };
    }

    return {
      top: onTop ? "" : "100%",
      left: alignRight ? undefined : (overflowRight ? -overflowRight : 0),
      bottom: onTop ? "100%" : "",
      maxHeight
    };
  }, [containerRef, modal, topOffset, portal, centered, alignRight, overflowRight, overflowBottom, overflowTop]);

  const windowMouseDownHandler = useCallback((event) => {
    if (modal) return;
    if (INSTANCES[INSTANCES.length - 1] !== dropdownRef) return;

    let parent = event.target;

    // eslint-disable-next-line no-loops/no-loops
    while (parent) {
      if (parent.isSameNode(dropdownRef.current)
        || (containerRef && parent.isSameNode(containerRef.current))
        || (toggleRef && parent.isSameNode(toggleRef.current))
      ) return;
      parent = parent.parentNode;
    }
    setOpened(false);
  }, [modal, setOpened, dropdownRef, containerRef, toggleRef]);

  const handleCloseClick = useCallback(() => {
    setOpened(false);
  }, [setOpened]);

  const windowResizeHandler = useCallback(() => {
    if (!Platforms.checkIsTouchDevice()) setOpened(false);
  }, [setOpened]);

  useLayoutEffect(() => {
    const { MOUSE_DOWN } = BrowserEvents;

    window.addEventListener(MOUSE_DOWN, windowMouseDownHandler);
    INSTANCES.push(dropdownRef);

    return () => {
      window.removeEventListener(MOUSE_DOWN, windowMouseDownHandler);
      INSTANCES.splice(INSTANCES.indexOf(dropdownRef), 1);
    };
  }, [setOpened, dropdownRef, windowMouseDownHandler]);
  useEffect(() => {
    const { width: dropdownWidth, top: dropdownTop, height: dropdownHeight } = dropdownRef.current.getBoundingClientRect();

    const { left } = containerRef.current.getBoundingClientRect();

    const contentIndent = Platforms.checkIsTouchDevice() ? 0 : CONTENT_INDENT;

    setOverflow({
      right: Math.max(left + dropdownWidth + contentIndent - window.innerWidth, 0),
      top: Math.min(dropdownTop, 0),
      bottom: Math.min(window.innerHeight - dropdownTop - dropdownHeight, 0)
    });
  }, [containerRef, dropdownRef, modal, windowMouseDownHandler]);
  useWindowResize(windowResizeHandler);

  const Wrapper = portal ? Portal : Fragment;

  return (
    <Wrapper>
      {modal && (
        <div className={Css.dropDownOverlay} />
      )}
      <div
        {...restProps}
        className={classNames(
          Css.dropDownContent,
          alignRight && Css.alignRight,
          modal && Css.modal,
          portal ? Css.portal : Css.relative,
          centered && Css.centered,
          className
        )}
        style={style}
        ref={dropdownRef}>
        {modal && (
          <div className={Css.header}>
            <div className={Css.title}>{title}</div>
            <div className={Css.close} onClick={handleCloseClick}>
              <Icons.X />
            </div>
          </div>
        )}
        {children}
      </div>
    </Wrapper>
  );
};

const DropDownContent = ({ disabled, onShow, ...restProps }) => {
  const { opened } = useContext(DropDownContext);

  useEffect(() => {
    if (opened && !disabled && onShow) onShow();
  }, [disabled, onShow, opened]);
  if (!opened || disabled) return null;

  return (
    <Content {...restProps} />
  );
};

export default React.memo(DropDownContent);
